import React, { Dispatch, SetStateAction, useContext, useEffect, useMemo, useState } from 'react'
import { Box, Chip, LinearProgress, Tooltip, Typography, useMediaQuery } from '@mui/material'
import { IEmission, IEmissionDetailHeaderPreview } from '../../../../shared/services/emission/emission.model'
import { IHeadCell } from '../../../../shared/interfaces/table-display.model'
import { TableDisplay } from '../../miscellaneous-components/table-display'
import { TableActionButtons } from '../../miscellaneous-components/table-action-buttons'
import gaiaTheme from '../../../../root-components/theme'
import { useNavigate } from 'react-router-dom'
import { createOrderChangeHandler } from '../../../../shared/utils/table-utils'
import { formatActivityDisplay, formatConsumptionFromCalculations, formatEmissionPeriodRange, formatEmissionTonnes, formatFactorDetailFromCalculations, resolveFlagFromCalculations, resolveSourceFromCalculations } from '../../../../shared/utils/emission-summary-utils'
import { SourceLabel } from '../../miscellaneous-components/source-label'
import { UserDetailsContext } from '../../../../context/user-details/user-details'
import LocationService from '../../../../shared/services/location/location.service'
import SubsidiaryService from '../../../../shared/services/subsidiary/subsidiary.service'
import { formatFlagCategoryDisplay } from '../../../../shared/constants/flag-emissions'
import { useFlagEmissionsCapability } from '../../../../hooks/use-flag-emissions-capability'
import { NotesOutlined } from '@mui/icons-material'
import NoteService from '../../../../shared/services/note/note.service'
import { INoteCounts } from '../../../../shared/services/note/note.model'

function dataQualityBarColor(percentRounded: number) {
  if (percentRounded < 30) return '#d3dfb8'
  if (percentRounded < 80) return gaiaTheme.palette.primary.light
  return gaiaTheme.palette.primary.main
}

interface IEmissionsTableProps {
  emissions: IEmission[]
  inputPage: number
  setInputPage: Dispatch<SetStateAction<number>>
  totalRecords: number
  perPage: number
  setPerPage: Dispatch<SetStateAction<number>>
  setOrderBy: Dispatch<SetStateAction<string | undefined>>
  setOrder: Dispatch<SetStateAction<'asc' | 'desc' | undefined>>
  selected?: string[]
  setSelected?: Dispatch<SetStateAction<string[]>>
  includeCheckbox?: boolean
  /** Full filtered dataset is selected (explicit opt-in). */
  selectAllFiltered?: boolean
  onExitSelectAllFiltered?: (remainingIdsOnPage: string[]) => void
  onDeleteRow?: (id: string) => void
  onEditRow?: (id: string) => void
  viewPath?: string
  backLink?: string
  backText?: string
  loading?: boolean
  /** Whole-list totals strip, rendered next to the pagination (see TableDisplay `summary`). */
  summary?: React.ReactNode
}

export const EmissionsTable = ({
  emissions,
  inputPage,
  setInputPage,
  totalRecords,
  perPage,
  setPerPage,
  setOrderBy,
  setOrder,
  selected: selectedProp,
  setSelected: setSelectedProp,
  includeCheckbox = false,
  selectAllFiltered = false,
  onExitSelectAllFiltered,
  onDeleteRow,
  onEditRow,
  viewPath = '/emissions/overview',
  backLink,
  backText,
  loading = false,
  summary,
}: IEmissionsTableProps) => {
  
  const navigate = useNavigate()
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'))
  const { flagEnabled } = useFlagEmissionsCapability()
  const userDetailsContext = useContext(UserDetailsContext)
  const roles = userDetailsContext.userDetails?.roles || []
  const isAdmin = roles.includes('admin')
  const isAnalystOnly = roles.includes('analyst') && !isAdmin
  const locationService = useMemo(() => LocationService.getInstance(), [])
  const subsidiaryService = useMemo(() => SubsidiaryService.getInstance(), [])
  const allowedLocationIds = locationService.locations?.map((l) => l.id)
  const allowedSubsidiaryIds = subsidiaryService.subsidiaries?.map((s) => s.id)
  const [scopeCacheLoaded, setScopeCacheLoaded] = useState(() => {
    if (!isAnalystOnly) return true
    return Boolean(locationService.locations) && Boolean(subsidiaryService.subsidiaries)
  })

  // Note indicators: one counts request per page of rows, never note text in a column. Clients
  // have no notes access, so they never ask.
  const noteService = useMemo(() => new NoteService(), [])
  const canReadNotes = roles.some((role) => role === 'admin' || role === 'analyst' || role === 'data-owner')
  const [noteCounts, setNoteCounts] = useState<INoteCounts>({})
  const emissionIdsKey = emissions.map((emission) => emission.id).join(',')

  useEffect(() => {
    if (!canReadNotes || !emissionIdsKey) {
      setNoteCounts({})
      return
    }
    let isActive = true
    noteService.getCounts('emission', emissionIdsKey.split(',').slice(0, 200))
      .then((response) => {
        if (isActive) setNoteCounts(response)
      })
      // Silent: an indicator that fails to load just does not show.
      .catch(() => {
        if (isActive) setNoteCounts({})
      })
    return () => {
      isActive = false
    }
  }, [canReadNotes, emissionIdsKey, noteService])

  const [internalSelected, setInternalSelected] = useState<string[]>([])
  const selected = selectedProp ?? internalSelected
  const setSelected = setSelectedProp ?? setInternalSelected

  useEffect(() => {
    if (!isAnalystOnly) return
    Promise.allSettled([locationService.getAllLocations(), subsidiaryService.getAll()])
      .then(() => setScopeCacheLoaded(true))
      .catch(() => setScopeCacheLoaded(true))
  }, [isAnalystOnly, locationService, subsidiaryService])

  const canWriteForEmission = (emission: IEmission): boolean => {
    if (!isAnalystOnly) return true

    // Use in-memory service caches. If cache isn't loaded yet, hide write actions (secure by default).
    if (!scopeCacheLoaded) return false

    if (emission.locationId) {
      if (!Array.isArray(allowedLocationIds) || !allowedLocationIds.includes(emission.locationId)) return false
    }
    if (emission.subsidiaryId) {
      if (!Array.isArray(allowedSubsidiaryIds) || !allowedSubsidiaryIds.includes(emission.subsidiaryId)) return false
    }
    return true
  }

  const headCells: IHeadCell[] = [
    { id: 'action', numeric: false, label: '', sortable: false },
    { id: 'activityLabel', numeric: false, label: 'Activity', sortable: true },
    { id: 'totalEmissionsKg', numeric: true, label: 'Emissions', sortable: true, align: 'right' },
    { id: 'consumption', numeric: true, label: 'Consumption', sortable: false, align: 'right' },
    { id: 'methodLabel', numeric: false, label: 'Method', sortable: true },
    { id: 'primaryDataPercent', numeric: false, label: 'Data quality', sortable: true },
    { id: 'source', numeric: false, label: 'Source', sortable: false },
    { id: 'startDate', numeric: false, label: 'Date', sortable: true },
  ]

  const handleView = (id: string) => {
    const params = new URLSearchParams()
    if (backLink) params.set('backLink', backLink)
    if (backText) params.set('backText', backText)
    const query = params.toString()
    const row = emissions.find(e => e.id === id)
    const emissionPreview: IEmissionDetailHeaderPreview | undefined = row
      ? {
          id: row.id,
          activityLabel: row.activityLabel,
          activityContext: row.activityContext,
          totalEmissionsKg: row.totalEmissionsKg,
          methodLabel: row.methodLabel,
          primaryDataPercent: row.primaryDataPercent,
          startDate: row.startDate,
          endDate: row.endDate,
        }
      : undefined
    navigate(`${viewPath}/${id}${query ? `?${query}` : ''}`, {
      state: emissionPreview ? { emissionPreview } : undefined,
    })
  }

  const rows = emissions.map(emission => {
    const canWrite = canWriteForEmission(emission)
    const flag = resolveFlagFromCalculations(emission.emissionCalculations)
    const notes = noteCounts[emission.id]
    const factorDetail = formatFactorDetailFromCalculations(emission.emissionCalculations)
    const origin = resolveSourceFromCalculations(emission.emissionCalculations)
    return ({
      id: emission.id,
    // The chip shares the activity column's existing width rather than adding a column: the label
    // flexes and truncates, so a flagged row costs no extra table width.
    activityLabel: (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 210, maxWidth: 270 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant='body2' sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {formatActivityDisplay(emission.activityLabel, emission.activityContext)}
          </Typography>
          {/* The factor tells apart rows that share a category (landfill vs recycling under one
              "Waste Management"). Wraps to two lines so the disposal method at the end survives. */}
          {factorDetail && (
            <Tooltip title={factorDetail} enterDelay={500}>
              <Typography
                variant='caption'
                color='text.secondary'
                component='div'
                sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.35 }}
              >
                {factorDetail}
              </Typography>
            </Tooltip>
          )}
        </Box>
        {notes && notes.count > 0 && (
          <Tooltip title={`${notes.count} ${notes.count === 1 ? 'note' : 'notes'}. Latest: ${notes.latest.body}`}>
            <Box
              aria-label={`${notes.count} ${notes.count === 1 ? 'note' : 'notes'}`}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.25, color: 'text.secondary', flexShrink: 0 }}
            >
              <NotesOutlined sx={{ fontSize: 16 }} />
              <Typography variant='caption'>{notes.count}</Typography>
            </Box>
          </Tooltip>
        )}
        {flagEnabled && flag.isFlag && (
          <Tooltip
            title={flag.flagCategory
              ? `FLAG: ${formatFlagCategoryDisplay(flag.flagCategory)}`
              : 'FLAG: no category set'}
          >
            <Chip size='small' label='FLAG' variant='outlined' color='warning' sx={{ flexShrink: 0 }} />
          </Tooltip>
        )}
      </Box>
    ),
    totalEmissionsKg: (
      <Box sx={{ minWidth: 180 }}>
        <Typography variant='body2' sx={{ textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 600 }}>
          {formatEmissionTonnes(emission.totalEmissionsKg)}
        </Typography>
      </Box>
    ),
    consumption: (
      <Box sx={{ minWidth: 140 }}>
        <Typography variant='body2' sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
          {formatConsumptionFromCalculations(emission.emissionCalculations)}
        </Typography>
      </Box>
    ),
    methodLabel: emission.methodLabel
      ? <Box sx={{ minWidth: 144 }}><Chip
        label={emission.methodLabel}
        size='small'
        color='primary'
        variant='outlined'
        sx={{
          width: 128,
          ...(emission.methodLabel === 'Spend-based'
            ? {
              borderColor: '#9e9e9e',
              color: '#757575',
            }
            : emission.methodLabel === 'Hybrid'
              ? {
                borderColor: '#9e9e9e',
                color: '#616161',
              }
              : undefined),
        }}
      /></Box>
      : <Box sx={{ minWidth: 144 }}>—</Box>,
    primaryDataPercent: (
      <Box sx={{ minWidth: 200 }}>
        {typeof emission.primaryDataPercent === 'number' ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LinearProgress
              variant='determinate'
              value={Math.max(0, Math.min(100, emission.primaryDataPercent))}
              sx={{
                flex: 1,
                height: 8,
                borderRadius: 999,
                backgroundColor: 'lightGrey.main',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: dataQualityBarColor(Math.round(emission.primaryDataPercent)),
                },
              }}
            />
            <Typography variant='body2' sx={{ minWidth: 42, textAlign: 'right', whiteSpace: 'nowrap' }}>
              {Math.round(emission.primaryDataPercent)}%
            </Typography>
          </Box>
        ) : (
          <Typography variant='body2'>—</Typography>
        )}
      </Box>
    ),
    // Where the record came from: the ledger for an integration, the kind of entry otherwise.
    source: <SourceLabel providerCode={origin.providerCode} source={origin.source} />,
    startDate: <Box sx={{ minWidth: 220, whiteSpace: 'nowrap' }}>{formatEmissionPeriodRange(emission.startDate, emission.endDate)}</Box>,
    action: (
      <TableActionButtons
        id={emission.id}
        preferViewThenAdditionalThenDelete={true}
        includeView={true}
        includeEdit={!!onEditRow && canWrite}
        includeDelete={!!onDeleteRow && canWrite}
        onView={(id: string) => handleView(id)}
        onEdit={(id: string) => onEditRow?.(id)}
        onDelete={(id: string) => onDeleteRow?.(id)}
        disableButton={false}
      />
    ),
    })
  })

  return (
    <Box>
      <TableDisplay
        onOrderChange={createOrderChangeHandler(setOrderBy, setOrder)}
        selectByIcon={true}
        isMobileMode={isMobileMode}
        mobileCardVariant="emissions"
        headCells={headCells}
        rows={rows}
        onRowClick={handleView}
        selected={selected}
        setSelected={setSelected}
        selectByCheckboxOnly={true}
        haveCheckboxSelect={includeCheckbox}
        inputPage={inputPage}
        setInputPage={setInputPage}
        totalRecords={totalRecords}
        perPage={perPage}
        setPerPage={setPerPage}
        selectAllFiltered={selectAllFiltered}
        onExitSelectAllFiltered={onExitSelectAllFiltered}
        loading={loading}
        summary={summary}
      />
    </Box>
  )
}
